import { createModelo, getModeloById, updateModelo } from "@spark/services/frotas";
import React, { useMemo, useCallback, useState, useEffect } from "react";
import { Form } from "react-final-form";
import { useHistory, useParams } from "react-router-dom";
import { Button, ButtonWithLoading } from "@spark/ui-components/components/Button";
import { Card, CardBody } from "@spark/ui-components/components/Card";
import { FormButtonToolbar, FormContainer } from "@spark/ui-components/components/form/FormElements";
import { formStructure, formSections, validateForm } from "./formConfig";
import DynamicForm from "@spark/ui-components/components/form/DynamicForm";
import { notification } from "@spark/ui-components/utils/notification";

// ---------- Converters ----------
const parseDecimal = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  if (typeof v === "number") return v;
  const s = String(v).replace(/\./g, "").replace(",", ".");
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
};

const toInt = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? 0 : n;
};

// ---------- Mappers ----------
const mapApiToForm = (m = {}) => ({
  Id: m.Id ?? "",
  Situacao: m.Situacao ?? "",
  Descricao: m.Descricao ?? "",
  CodigoInterno: m.CodigoInterno ?? "",
  CodigoIntegracao: m.CodigoIntegracao ?? "",
  NomeTipoModeloVeicular: m.NomeTipoModeloVeicular ?? "",
  CodigoTipoModeloVeicular: m.CodigoTipoModeloVeicular ?? "",
  UnidadeCapacidade: m.UnidadeCapacidade ?? "",
  CapacidadeModelo: m.CapacidadeModelo ?? "",
  NumeroEixos: m.NumeroEixos ?? "",
  OcupacaoCubicaPallets: m.OcupacaoCubicaPallets ?? "",
  OcupacaoCubicaMaxima: m.OcupacaoCubicaMaxima ?? "",
  QuantidadePallet: m.QuantidadePallet ?? "",
  CapacidadeMaximoPallets: m.CapacidadeMaximoPallets ?? "",
  ToleranciaMinimaPallets: m.ToleranciaMinimaPallets ?? "",
  ToleranciaMinimaCarregamento: m.ToleranciaMinimaCarregamento ?? "",
  QuantidadeExtraExcedenteTolerado: m.QuantidadeExtraExcedenteTolerado ?? "",
  ToleranciaExtra: m.ToleranciaExtra ?? "",
  CodigoTipoCargaANTT: m.CodigoTipoCargaANTT ?? "",
});

const mapFormToApi = (values) => ({
  Descricao: values.Descricao,
  CodigoInterno: toInt(values.CodigoInterno),
  CodigoIntegracao: toInt(values.CodigoIntegracao),
  NomeTipoModeloVeicular: values.NomeTipoModeloVeicular || null,
  CodigoTipoModeloVeicular: values.CodigoTipoModeloVeicular || null,
  UnidadeCapacidade: values.UnidadeCapacidade || null,
  CodigoTipoCargaANTT: values.CodigoTipoCargaANTT || null,

  CapacidadeModelo: toInt(values.CapacidadeModelo),
  NumeroEixos: toInt(values.NumeroEixos),
  OcupacaoCubicaPallets: parseDecimal(values.OcupacaoCubicaPallets),
  OcupacaoCubicaMaxima: parseDecimal(values.OcupacaoCubicaMaxima),
  QuantidadePallet: parseDecimal(values.QuantidadePallet),
  CapacidadeMaximoPallets: parseDecimal(values.CapacidadeMaximoPallets),
  ToleranciaMinimaPallets: toInt(values.ToleranciaMinimaPallets),
  ToleranciaMinimaCarregamento: parseDecimal(values.ToleranciaMinimaCarregamento),
  QuantidadeExtraExcedenteTolerado: toInt(values.QuantidadeExtraExcedenteTolerado),
  ToleranciaExtra: parseDecimal(values.ToleranciaExtra),
});

// ---------- Component ----------
const Forms = ({ isReadOnly = false }) => {
  const history = useHistory();
  const { id } = useParams();

  console.log(history)

  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  const isEditMode = !!id && !isReadOnly;

  // Defaults em PascalCase (iguais aos names dos campos)
  const defaultValues = useMemo(
    () => ({
      Id: "",
      Situacao: "",
      Descricao: "",
      CodigoInterno: "",
      CodigoIntegracao: "",
      NomeTipoModeloVeicular: "",
      CodigoTipoModeloVeicular: "",
      UnidadeCapacidade: "",
      CapacidadeModelo: "",
      NumeroEixos: "",
      OcupacaoCubicaPallets: "",
      OcupacaoCubicaMaxima: "",
      QuantidadePallet: "",
      CapacidadeMaximoPallets: "",
      ToleranciaMinimaPallets: "",
      ToleranciaMinimaCarregamento: "",
      QuantidadeExtraExcedenteTolerado: "",
      ToleranciaExtra: "",
      CodigoTipoCargaANTT: "",
    }),
    []
  );

  // Carregar dados quando for modo de visualização ou edição
  useEffect(() => {
    if ((isReadOnly || isEditMode) && id) {
      const loadData = async () => {
        try {
          setLoadingData(true);
          const resp = await getModeloById(id);
          const modelo = resp?.modelo || resp || {};
          setInitialData(mapApiToForm(modelo));
        } catch (error) {
          console.error("Erro ao carregar modelo:", error);
        } finally {
          setLoadingData(false);
        }
      };

      loadData();
    } else {
      // novo registro
      setInitialData(defaultValues);
    }
  }, [isReadOnly, isEditMode, id, defaultValues]);

  const onSubmit = useCallback(
    async (values) => {
      try {
        setLoading(true);
        const payload = mapFormToApi(values);

        if (isEditMode) {
          await updateModelo(id, payload);
          notification.success("Modelo atualizado com sucesso!");
        } else {
          await createModelo(payload);
          notification.success("Modelo criado com sucesso!");
        }

        history.goBack();
      } catch (err) {
        console.error("Erro ao salvar modelo:", err);
      } finally {
        setLoading(false);
      }
    },
    [history, id, isEditMode]
  );

  const currentInitialValues = initialData || defaultValues;

  if ((isReadOnly || isEditMode) && loadingData) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: "center", padding: "50px" }}>
            Carregando dados do modelo...
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Form
      key={id || "novo"}                 // força re-mount ao trocar o registro
      onSubmit={onSubmit}
      initialValues={currentInitialValues}
      validate={validateForm}
      keepDirtyOnReinitialize={false}    // comportamento padrão; pode remover
    >
      {({ handleSubmit }) => (
        <Card>
          <CardBody>
            <FormContainer onSubmit={handleSubmit}>
              <DynamicForm
                structure={formStructure}
                sections={formSections(currentInitialValues)}
                isViewMode={isReadOnly}
                isEditing={isEditMode}
              />

              {!isReadOnly && (
                <FormButtonToolbar className="mt-5">
                  <ButtonWithLoading
                    variant="primary"
                    type="submit"
                    loading={loading || undefined} // evita string "true"
                  >
                    {isEditMode ? "Atualizar" : "Salvar"}
                  </ButtonWithLoading>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => history.goBack()}
                  >
                    Cancelar
                  </Button>
                </FormButtonToolbar>
              )}
            </FormContainer>
          </CardBody>
        </Card>
      )}
    </Form>
  );
};

export default Forms;
